// ============================================================================
// /people/directory/:group — ONE card for one person, showing every "hat"
// they wear (Ben, 2026-10-06, item 818): the salaried record, the hourly
// (punch) record, the sub or 1099 record. Hats are joined only by the link
// fields that already exist on the worker row (decision 1A).
//
// What the card edits, and where it saves:
//   * Hourly record — phone, email, preferred language, status: fields that
//     already exist on `workers`, saved back to that row. Nothing else.
//   * Salaried record — opens the existing employee card; it is not edited
//     here a second time. "Start separation" opens the existing flow.
//   * Sub record — opens the existing Subcontractors hub record; sub changes
//     go through the hub's own flows (Ben, 2026-10-03).
//
// The clock-in code control is the Time & Labor one (PinShown + the same
// database door). It stays off until a mobile number is on the record — a
// code is useless to someone who cannot sign in with a phone.
// ============================================================================
import { canWriteHrRecord } from '../../shared/role-gates';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowUpRight, KeyRound, Phone, PhoneOff, UserMinus } from 'lucide-react';
import { Avatar, Button, Card, ErrorState, PageHeader, Skeleton, useToast } from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { errorMessage } from '../../shared/errors';
import { issueWorkerPin } from '../time-labor/hooks/use-am-data';
import { useEmployerOptions } from '../time-labor/hooks/use-time-data';
import { PinShown } from '../time-labor/pin-shown';
import type { WorkerLang } from '../time-labor/types-am';
import { HatChip } from './directory-everyone';
import {
  updateWorkerContact,
  usePerson,
  workerHasPin,
  type PersonRow,
  type WorkerContactPatch,
} from './hooks/use-people-directory';

const DASH = '—';

/** The worker's language list, exactly as the database CHECK allows it. */
const LANGUAGES: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'es', label: 'Spanish' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'French' },
  { code: 'ht', label: 'Haitian Creole' },
  { code: 'wo', label: 'Wolof' },
];
const WORKER_STATUSES: ReadonlyArray<{ code: WorkerContactPatch['worker_status']; label: string }> = [
  { code: 'active', label: 'Active' },
  { code: 'inactive', label: 'Inactive' },
  { code: 'terminated', label: 'Terminated' },
];

export default function PersonCard() {
  const { group: raw } = useParams<{ group: string }>();
  const group = raw ? decodeURIComponent(raw) : undefined;
  const { profile } = useAuth();
  const canWrite = canWriteHrRecord(profile);
  const { person, loading, error, refetch } = usePerson(group);

  if (error) return <ErrorState title="Couldn't load this person" body={error} />;
  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-3 p-4 md:p-6">
        <Skeleton className="h-10" />
        <Skeleton className="h-40" />
      </div>
    );
  }
  if (!person) {
    return (
      <div className="mx-auto max-w-4xl p-4 md:p-6">
        <ErrorState
          title="Nobody by that record"
          body="This link does not point at anyone in the directory. They may have been removed, or the link is old."
        />
        <Link to="/people/directory" className="mt-3 inline-block text-sm underline">
          Back to People
        </Link>
      </div>
    );
  }

  const phone = person.hats.find((h) => h.phone)?.phone ?? null;

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 md:p-6">
      <PageHeader
        eyebrow={
          <Link to="/people/directory" className="hover:underline">
            People › directory
          </Link>
        }
        title={
          <span className="flex items-center gap-3">
            <Avatar name={person.name} size="lg" />
            <span>{person.name}</span>
          </span>
        }
        meta={
          <span className="flex flex-wrap items-center gap-1.5">
            {person.hats.map((h) => (
              <HatChip key={h.person_key ?? `${h.source}:${h.record_id}`} hat={h} />
            ))}
            <span className="ml-1 inline-flex items-center gap-1 text-xs text-tcs-text-secondary">
              {phone ? (
                <>
                  <Phone className="h-3 w-3" /> {phone}
                </>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-st-warn-bg px-2 py-0.5 text-st-warn-fg-strong">
                  <PhoneOff className="h-3 w-3" /> No phone
                </span>
              )}
            </span>
          </span>
        }
      />

      {person.hats.map((h) => {
        const key = h.person_key ?? `${h.source}:${h.record_id}`;
        if (h.source === 'worker') return <WorkerHat key={key} hat={h} canWrite={canWrite} onSaved={refetch} />;
        if (h.source === 'employee') return <EmployeeHat key={key} hat={h} canWrite={canWrite} />;
        return <ProviderHat key={key} hat={h} />;
      })}
    </div>
  );
}

// ----- the salaried record: a link to its own card ---------------------------

function EmployeeHat({ hat, canWrite }: { hat: PersonRow; canWrite: boolean }) {
  const id = hat.record_id ?? '';
  return (
    <Card className="p-4">
      <HatHeader title="Salaried record" id={id} status={hat.status} separated={!!hat.is_separated} />
      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <Row label="Title" value={hat.title ?? DASH} />
        <Row label="Pay type" value={hat.pay_type ?? 'Not set'} />
        <Row label="Work email" value={hat.email ?? DASH} />
        <Row label="Phone" value={hat.phone ?? DASH} />
        <Row label="Hired" value={hat.start_date ?? DASH} />
        {hat.is_separated && <Row label="Separated" value={hat.end_date ?? DASH} />}
      </dl>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Link to={`/people/hr/directory/${id}`}>
          <Button size="sm" variant="secondary" icon={<ArrowUpRight className="h-3.5 w-3.5" />}>
            Open the employee record
          </Button>
        </Link>
        {canWrite && !hat.is_separated && (
          <Link to={`/people/separations/new?employee=${encodeURIComponent(id)}`}>
            <Button size="sm" variant="secondary" icon={<UserMinus className="h-3.5 w-3.5" />}>
              Start separation
            </Button>
          </Link>
        )}
      </div>
      <p className="mt-2 text-xs text-tcs-text-muted">
        Everything on a salaried record — compensation, benefits, work state, address — is edited on
        the employee record, as before.
      </p>
    </Card>
  );
}

// ----- the sub / 1099 record: a link into the hub ----------------------------

function ProviderHat({ hat }: { hat: PersonRow }) {
  const id = hat.record_id ?? '';
  const is1099 = hat.person_type === '1099';
  return (
    <Card className="p-4">
      <HatHeader
        title={is1099 ? '1099 individual record' : 'Sub owner record'}
        id={id}
        status={hat.status}
        separated={!!hat.is_separated}
      />
      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        {!is1099 && <Row label="Company" value={hat.company_name ?? DASH} />}
        <Row label="Phone" value={hat.phone ?? DASH} />
        <Row label="Email" value={hat.email ?? DASH} />
        <Row label="With TCS since" value={hat.start_date ?? DASH} />
      </dl>
      <div className="mt-3">
        <Link to={`/people/subcontractors/${id}`}>
          <Button size="sm" variant="secondary" icon={<ArrowUpRight className="h-3.5 w-3.5" />}>
            Open in Subcontractors
          </Button>
        </Link>
      </div>
      <p className="mt-2 text-xs text-tcs-text-muted">
        Sub details change in the Subcontractors hub — contacts, documents, insurance, buildings —
        the same way they always have.
      </p>
    </Card>
  );
}

// ----- the hourly record: the fields that already exist, saved back to it ---

function WorkerHat({ hat, canWrite, onSaved }: { hat: PersonRow; canWrite: boolean; onSaved: () => void }) {
  const toast = useToast();
  const workerId = hat.record_id ?? '';
  const [phone, setPhone] = useState(hat.phone ?? '');
  const [email, setEmail] = useState(hat.email ?? '');
  const [lang, setLang] = useState(hat.preferred_language ?? 'es');
  const [status, setStatus] = useState<WorkerContactPatch['worker_status']>(
    (hat.status as WorkerContactPatch['worker_status']) ?? 'active',
  );
  // Works for: '' = TCS. A company makes them Subcontractor's crew — never on TCS payroll.
  const employers = useEmployerOptions();
  const [employer, setEmployer] = useState(hat.employer_provider_id ?? '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [pin, setPin] = useState<string | null>(null);
  // Honest since decision 108 (item #822): the picker's has_pin reads a definer
  // function, so an app user sees the truth. Re-read after a code is issued.
  const [pinOnRecord, setPinOnRecord] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    void workerHasPin(workerId).then((r) => {
      if (!cancelled) setPinOnRecord(r ? r.set : null);
    });
    return () => {
      cancelled = true;
    };
  }, [workerId, pin]);
  const [pinError, setPinError] = useState<string | null>(null);

  useEffect(() => {
    setPhone(hat.phone ?? '');
    setEmail(hat.email ?? '');
    setLang(hat.preferred_language ?? 'es');
    setStatus((hat.status as WorkerContactPatch['worker_status']) ?? 'active');
    setEmployer(hat.employer_provider_id ?? '');
  }, [hat.phone, hat.email, hat.preferred_language, hat.status, hat.employer_provider_id]);
  const employerChanged = employer !== (hat.employer_provider_id ?? '');

  const dirty =
    phone.trim() !== (hat.phone ?? '') ||
    email.trim() !== (hat.email ?? '') ||
    lang !== (hat.preferred_language ?? 'es') ||
    status !== ((hat.status as WorkerContactPatch['worker_status']) ?? 'active') ||
    employerChanged;
  // A subcontractor's cleaner signs in on the building's kiosk by tapping her name, so her
  // code needs no phone; everyone else signs in on the clock with phone + code.
  const codeCanBeUsed = !!hat.has_phone || !!hat.employer_provider_id;

  async function save() {
    setSaving(true);
    setSaveError(null);
    try {
      await updateWorkerContact(workerId, {
        phone_mobile: phone.trim() || null,
        email: email.trim() || null,
        preferred_language: lang,
        worker_status: status,
        // The type follows Works for, in the same save, so the two always agree.
        ...(employerChanged
          ? { employer_provider_id: employer || null,
              employment_class: employer ? 'subcontractor_crew' : 'hourly_paid_employee' }
          : {}),
      });
      toast.success('Saved');
      onSaved();
    } catch (e) {
      setSaveError(errorMessage(e, 'Could not save.'));
    } finally {
      setSaving(false);
    }
  }

  async function issue() {
    setIssuing(true);
    setPinError(null);
    try {
      const code = await issueWorkerPin(workerId);
      setPin(code);
    } catch (e) {
      setPinError(errorMessage(e, 'Could not issue a code.'));
    } finally {
      setIssuing(false);
    }
  }

  const field =
    'mt-1 w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-accent focus:outline-none disabled:opacity-70';

  return (
    <Card className="p-4">
      <HatHeader title="Hourly record" id={workerId} status={hat.status} separated={!!hat.is_separated} />
      {hat.company_name && (
        <p className="mt-1 text-xs text-tcs-text-secondary">Paid through {hat.company_name}.</p>
      )}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-xs text-tcs-text-secondary sm:col-span-2">
          Works for
          <select value={employer} disabled={!canWrite} onChange={(e) => setEmployer(e.target.value)} className={field}>
            <option value="">TCS — paid by TCS</option>
            {employer && !employers.rows.some((o) => o.provider_id === employer) && (
              <option value={employer}>{hat.company_name ?? employer}</option>
            )}
            {employers.rows.map((o) => (
              <option key={o.provider_id} value={o.provider_id}>{o.display}</option>
            ))}
          </select>
          {employerChanged && (
            <span className="mt-1 block text-tcs-text-muted">
              {employer
                ? "Saving makes them Subcontractor's crew: off every TCS payroll page, on that company's pay sheet."
                : 'Saving makes them an Hourly Paid Employee, paid by TCS.'}
            </span>
          )}
          {employers.error && <span className="mt-1 block text-st-danger-fg">{employers.error}</span>}
        </label>
        <label className="block text-xs text-tcs-text-secondary">
          Mobile number
          <input
            type="tel"
            value={phone}
            disabled={!canWrite}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="No phone on file"
            className={field}
          />
        </label>
        <label className="block text-xs text-tcs-text-secondary">
          Email
          <input type="email" value={email} disabled={!canWrite} onChange={(e) => setEmail(e.target.value)} className={field} />
        </label>
        <label className="block text-xs text-tcs-text-secondary">
          Preferred language
          <select value={lang} disabled={!canWrite} onChange={(e) => setLang(e.target.value)} className={field}>
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-tcs-text-secondary">
          Status
          <select
            value={status}
            disabled={!canWrite}
            onChange={(e) => setStatus(e.target.value as WorkerContactPatch['worker_status'])}
            className={field}
          >
            {WORKER_STATUSES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {canWrite && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" loading={saving} disabled={!dirty} onClick={() => void save()}>
            Save
          </Button>
          {dirty && !saving && <span className="text-xs text-tcs-text-muted">Unsaved changes</span>}
          {saveError && <span className="text-xs text-st-danger-fg">{saveError}</span>}
        </div>
      )}
      {!canWrite && (
        <p className="mt-2 text-xs text-tcs-text-muted">HR edits these fields; you can read them.</p>
      )}
      <p className="mt-2 text-xs text-tcs-text-muted">
        An hourly worker has no separation checklist — their status is set here. Hours and pay stay in
        Time &amp; Labor.
      </p>

      {/* ----- the clock-in code: Time & Labor's own control ----- */}
      <div className="mt-4 border-t border-tcs-border pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <KeyRound className="h-4 w-4 text-tcs-text-muted" />
          <span className="text-sm font-medium text-tcs-text">Clock-in code</span>
          <span className="text-xs text-tcs-text-secondary">
            {pinOnRecord === null ? '' : pinOnRecord ? 'A code is on record.' : 'No code yet.'}
          </span>
          <Button
            size="sm"
            variant="secondary"
            loading={issuing}
            disabled={!codeCanBeUsed || !!hat.is_separated}
            onClick={() => void issue()}
          >
            {pinOnRecord ? 'Reset the code' : 'Issue a code'}
          </Button>
          {!codeCanBeUsed && (
            <span className="text-xs text-st-warn-fg-strong">Needs a mobile number on record first.</span>
          )}
          {pinError && <span className="text-xs text-st-danger-fg">{pinError}</span>}
        </div>
        {pin && (
          <PinShown
            name={hat.display_name ?? workerId}
            pin={pin}
            lang={(lang === 'en' ? 'en' : 'es') as WorkerLang}
            onDone={() => setPin(null)}
          />
        )}
      </div>
    </Card>
  );
}

// ----- small parts -----------------------------------------------------------

function HatHeader({ title, id, status, separated }: { title: string; id: string; status: string | null; separated: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-semibold text-tcs-text">{title}</span>
      <span className="font-mono text-[11px] text-tcs-text-muted">{id}</span>
      <span
        className={`rounded-full px-2 py-0.5 text-[11px] ${
          separated ? 'bg-tcs-border text-tcs-text-secondary' : 'bg-st-ok-bg text-st-ok-fg-strong'
        }`}
      >
        {status ?? DASH}
      </span>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-xs text-tcs-text-muted">{label}</dt>
      <dd className="text-sm text-tcs-text">{value}</dd>
    </>
  );
}
